import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ApiError,
  AsyncDataItem,
  AsyncDataItemState,
  IBookableService,
  IBookableServiceListParams,
  IPaginationMeta,
  idleDataItem,
  isError,
  isLoading,
  wrapAsAsyncDataItem
} from '@pharvio/core/shared';
import { EMPTY, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BookableServicesDataService } from '../../data/repositories/bookable-services-data.service';
import { BOOKABLE_SERVICES_DATA_SERVICE } from '../../data/tokens/bookable-services-service.token';
import {
  CreateBookableService,
  DeleteBookableService,
  LoadBookableService,
  LoadBookableServices,
  SelectBookableService,
  UpdateBookableService
} from './bookable-services.actions';

export interface BookableServicesStateModel {
  services: AsyncDataItem<IBookableService[]>;
  pagination: IPaginationMeta | null;
  /** The filter the current page was fetched with, for reload-in-place. */
  params: IBookableServiceListParams;
  selectedId: string | null;
}

@State<BookableServicesStateModel>({
  name: 'bookableServices',
  defaults: {
    services: idleDataItem([]),
    pagination: null,
    params: {},
    selectedId: null
  }
})
@Injectable()
export class BookableServicesState {
  private readonly service: BookableServicesDataService = inject(
    BOOKABLE_SERVICES_DATA_SERVICE
  );

  @Selector() static services(
    s: BookableServicesStateModel
  ): AsyncDataItem<IBookableService[]> {
    return s.services;
  }

  @Selector() static loading(s: BookableServicesStateModel): boolean {
    return isLoading(s.services);
  }

  @Selector() static error(s: BookableServicesStateModel): ApiError | null {
    return isError(s.services) ? s.services.error : null;
  }

  @Selector() static pagination(
    s: BookableServicesStateModel
  ): IPaginationMeta | null {
    return s.pagination;
  }

  @Selector() static params(
    s: BookableServicesStateModel
  ): IBookableServiceListParams {
    return s.params;
  }

  @Selector() static selectedId(s: BookableServicesStateModel): string | null {
    return s.selectedId;
  }

  /**
   * What the booking form may offer. A deactivated service is still listed on
   * the catalogue page (so it can be switched back on) but must never be
   * bookable.
   */
  @Selector() static bookableServices(
    s: BookableServicesStateModel
  ): IBookableService[] {
    return s.services.data.filter((row) => row.isActive);
  }

  @Action(LoadBookableServices)
  load(
    ctx: StateContext<BookableServicesStateModel>,
    { params }: LoadBookableServices
  ) {
    ctx.patchState({
      services: wrapAsAsyncDataItem({ data: ctx.getState().services.data }),
      params
    });

    return this.service.getServices(params).pipe(
      tap((page) =>
        ctx.patchState({
          services: wrapAsAsyncDataItem({
            data: page.data,
            state: AsyncDataItemState.LOADED
          }),
          pagination: page.meta
        })
      ),
      catchError((error) => {
        ctx.patchState({
          services: wrapAsAsyncDataItem({
            data: [],
            state: AsyncDataItemState.ERROR,
            error
          }),
          pagination: null
        });
        return EMPTY;
      })
    );
  }

  /**
   * Puts one row in the list, for a caller that has an id and needs the
   * service behind it — a filter chip naming what it filters by, say.
   *
   * Skips the request when the list already holds it. Pass `reload` to
   * refresh a row in place regardless.
   */
  @Action(LoadBookableService)
  loadOne(
    ctx: StateContext<BookableServicesStateModel>,
    { id, reload }: LoadBookableService
  ) {
    if (!reload && ctx.getState().services.data.some((s) => s.id === id)) {
      return EMPTY;
    }
    return this.service.getService(id).pipe(
      tap((row) => this.upsert(ctx, row)),
      // A row nobody can resolve is not an error worth surfacing; the caller
      // renders nothing rather than a placeholder.
      catchError(() => EMPTY)
    );
  }

  // Mutations rethrow rather than swallowing: the form maps
  // SERVICE_NAME_TAKEN onto the name control and the list turns
  // SERVICE_HAS_OPEN_BOOKINGS into the "deactivate instead" prompt, neither of
  // which reaches the subscriber if the error dies here.

  @Action(CreateBookableService)
  create(
    ctx: StateContext<BookableServicesStateModel>,
    { payload }: CreateBookableService
  ) {
    return this.service.createService(payload).pipe(
      tap((row) => this.upsert(ctx, row)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(UpdateBookableService)
  update(
    ctx: StateContext<BookableServicesStateModel>,
    { id, payload }: UpdateBookableService
  ) {
    return this.service.updateService(id, payload).pipe(
      tap((row) => this.upsert(ctx, row)),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(DeleteBookableService)
  remove(
    ctx: StateContext<BookableServicesStateModel>,
    { id }: DeleteBookableService
  ) {
    return this.service.deleteService(id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.patchState({
          services: wrapAsAsyncDataItem({
            data: state.services.data.filter((row) => row.id !== id),
            state: AsyncDataItemState.LOADED
          }),
          selectedId: state.selectedId === id ? null : state.selectedId
        });
      }),
      catchError((error) => throwError(() => error))
    );
  }

  @Action(SelectBookableService)
  select(
    ctx: StateContext<BookableServicesStateModel>,
    { id }: SelectBookableService
  ) {
    ctx.patchState({ selectedId: id });
  }

  /**
   * Replaces the row when the list already holds it, prepends it otherwise.
   * The pagination meta is left alone: a created row shifts totals the server
   * owns, and the page reloads rather than guessing them.
   */
  private upsert(
    ctx: StateContext<BookableServicesStateModel>,
    row: IBookableService
  ): void {
    const current = ctx.getState().services.data;
    const inList = current.some((s) => s.id === row.id);
    ctx.patchState({
      services: wrapAsAsyncDataItem({
        data: inList
          ? current.map((s) => (s.id === row.id ? row : s))
          : [row, ...current],
        state: AsyncDataItemState.LOADED
      })
    });
  }
}
